import React from 'react';
import { Calendar, MapPin, Shield, Heart } from 'lucide-react';
import styled, { keyframes } from 'styled-components';

// Keyframe animations
const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(3deg);
  }
`;

const floatReverse = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(15px) rotate(-3deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

const slideIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(255, 99, 132, 0.4), 0 0 40px rgba(255, 99, 132, 0.2);
  }
  50% {
    box-shadow: 0 0 30px rgba(255, 99, 132, 0.6), 0 0 60px rgba(255, 99, 132, 0.4);
  }
`;

// Styled components
const SceneContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  background: linear-gradient(135deg, rgba(255, 99, 132, 0.05) 0%, rgba(236, 100, 53, 0.05) 50%, rgba(255, 159, 64, 0.05) 100%);
`;

const CalendarMockup = styled.div`
  position: relative;
  width: 240px;
  height: auto;
  min-height: 280px;
  background: linear-gradient(135deg, #FF6384 0%, #EC6435 100%);
  border-radius: 24px;
  box-shadow: 
    0 20px 50px rgba(0, 0, 0, 0.3),
    0 0 30px rgba(255, 99, 132, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  display: flex;
  flex-direction: column;
  padding: 20px;
  animation: 
    ${slideIn} 0.8s ease-out forwards,
    ${glowPulse} 4s ease-in-out infinite;
  z-index: 10;
  border: 2px solid rgba(255, 255, 255, 0.2);
`;

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const CalendarTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-bottom: 20px;
`;

const Day = styled.div<{ $active?: boolean; $delay: number }>`
  width: 100%;
  aspect-ratio: 1;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.15)'};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 700;
  color: ${props => props.$active ? '#FF6384' : '#ffffff'};
  animation: ${pulse} ${props => 2 + props.$delay}s ease-in-out infinite;
  
  ${props => props.$active && `
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.6);
    transform: scale(1.1);
  `}
`;

const FloatingElement = styled.div<{ $delay: number; $position: { top: string; left: string } }>`
  position: absolute;
  top: ${props => props.$position.top};
  left: ${props => props.$position.left};
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  padding: 10px 16px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: ${float} ${props => 4 + props.$delay}s ease-in-out infinite;
  z-index: 15;
  border: 1px solid rgba(255, 255, 255, 0.5);
  
  span {
    font-size: 11px;
    font-weight: 700;
    color: #1a1a1a;
  }
`;

const Particle = styled.div<{ $delay: number; $position: { top: string; left: string } }>`
  position: absolute;
  top: ${props => props.$position.top};
  left: ${props => props.$position.left};
  width: 4px;
  height: 4px;
  background: radial-gradient(circle, rgba(255, 99, 132, 0.8) 0%, transparent 70%);
  border-radius: 50%;
  animation: 
    ${floatReverse} ${props => 6 + props.$delay}s ease-in-out infinite,
    ${pulse} ${props => 4 + props.$delay}s ease-in-out infinite;
  opacity: 0.5;
  z-index: 0;
`;

const GlowEffect = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(255, 99, 132, 0.15) 0%, transparent 70%);
  border-radius: 50%;
  animation: ${pulse} 5s ease-in-out infinite;
  z-index: 0;
`;

export const DateAnimatedScene: React.FC = () => {
  const days = Array.from({ length: 21 }, (_, i) => ({
    id: i,
    active: i === 13 || i === 17,
    delay: Math.random() * 2
  }));

  const floatingElements = [
    {
      icon: <Heart size={16} color="#FF6384" fill="#FF6384" />,
      text: 'Perfect Match',
      delay: 0,
      position: { top: '15%', left: '5%' }
    },
    {
      icon: <MapPin size={16} color="#EC6435" />,
      text: 'Sunset View',
      delay: 1,
      position: { top: '75%', left: '55%' }
    },
    {
      icon: <Shield size={16} color="#4BC0C0" />,
      text: 'Verified Dates',
      delay: 2,
      position: { top: '25%', left: '65%' }
    },
  ];

  const particles = [
    { delay: 0, position: { top: '10%', left: '20%' } },
    { delay: 0.5, position: { top: '80%', left: '80%' } },
    { delay: 1, position: { top: '40%', left: '85%' } },
    { delay: 1.5, position: { top: '70%', left: '15%' } },
    { delay: 2, position: { top: '50%', left: '10%' } },
  ];

  return (
    <SceneContainer>
      <GlowEffect />

      {particles.map((particle, i) => (
        <Particle
          key={`particle-${i}`}
          $delay={particle.delay}
          $position={particle.position}
        />
      ))}

      <CalendarMockup>
        <CalendarHeader>
          <CalendarTitle>February 2026</CalendarTitle>
          <Calendar size={18} color="#ffffff" />
        </CalendarHeader>
        <CalendarGrid>
          {days.map((day) => (
            <Day key={day.id} $active={day.active} $delay={day.delay}>
              {day.id + 1}
            </Day>
          ))}
        </CalendarGrid>
        <div style={{ marginTop: '16px', fontSize: '10px', color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
          3 matches waiting...
        </div>
      </CalendarMockup>

      {floatingElements.map((el, i) => (
        <FloatingElement
          key={`float-${i}`}
          $delay={el.delay}
          $position={el.position}
        >
          {el.icon}
          <span>{el.text}</span>
        </FloatingElement>
      ))}
    </SceneContainer>
  );
};
